// SandTris.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";

export default function SandTris() {
  // Macro board
  const COLS = 10;
  const ROWS = 18;

  // Each macro cell is 4x4 grains
  const GRAIN = 4;
  const GCOLS = COLS * GRAIN;
  const GROWS = ROWS * GRAIN;

  // Visual size of each grain on canvas
  const SCALE = 6;

  // Piece fall timing (ms)
  const TICK_MS = 420;

  // Sand simulation steps per frame
  const SAND_ITERS_PER_FRAME = 2;

  // Clear animation duration (ms)
  const CLEAR_DUR_MS = 260;

  // How often to scan for clears while sand is settling (frames)
  const CLEAR_SCAN_EVERY_N_FRAMES = 6;

  const canvasRef = useRef(null);
  const rafRef = useRef(null);

  const lastTimeRef = useRef(0);
  const fallAccumRef = useRef(0);
  const frameRef = useRef(0);

  // React state for HUD (mirrors refs)
  const [score, setScore] = useState(0);
  const scoreRef = useRef(0);

  const [over, setOver] = useState(false);

  const COLORS = useMemo(
    () => ["#ff3b3b", "#ffd23b", "#3bff8f", "#3bd6ff", "#b03bff"],
    [],
  );

  const SHAPES = useMemo(
    () => [
      [
        [1, 1, 0],
        [0, 1, 1],
      ],
      [
        [1, 1],
        [1, 1],
      ],
      [
        [1, 0, 0],
        [1, 1, 1],
      ],
      [
        [0, 1, 1],
        [1, 1, 0],
      ],
      [
        [0, 1, 0],
        [1, 1, 1],
      ],
    ],
    [],
  );

  const emptyGrains = () =>
    Array.from({ length: GROWS }, () => Array(GCOLS).fill(null));

  // Simulation state in refs
  const grainsRef = useRef(emptyGrains());
  const pieceRef = useRef(null);
  const overRef = useRef(false);

  // { cells:Set("x,y"), t:number, dur:number, points:number } | null
  const clearingRef = useRef(null);

  function spawnPiece() {
    const shape = SHAPES[Math.floor(Math.random() * SHAPES.length)];
    const color = COLORS[Math.floor(Math.random() * COLORS.length)];
    const w = shape[0].length;
    return {
      shape,
      color,
      x: Math.floor((COLS - w) / 2) * GRAIN,
      y: 0,
    };
  }

  function rotate(shape) {
    const h = shape.length;
    const w = shape[0].length;
    const out = Array.from({ length: w }, () => Array(h).fill(0));
    for (let r = 0; r < h; r++) {
      for (let c = 0; c < w; c++) out[c][h - 1 - r] = shape[r][c];
    }
    return out;
  }

  function collidesGrains(g, p) {
    const { shape, x, y } = p;
    for (let r = 0; r < shape.length; r++) {
      for (let c = 0; c < shape[0].length; c++) {
        if (!shape[r][c]) continue;

        const baseX = x + c * GRAIN;
        const baseY = y + r * GRAIN;

        for (let gy = 0; gy < GRAIN; gy++) {
          for (let gx = 0; gx < GRAIN; gx++) {
            const X = baseX + gx;
            const Y = baseY + gy;
            if (X < 0 || X >= GCOLS || Y < 0 || Y >= GROWS) return true;
            if (g[Y][X] != null) return true;
          }
        }
      }
    }
    return false;
  }

  function stampPieceAsGrains(g, p) {
    const next = g.map((row) => row.slice());
    const { shape, x, y, color } = p;

    for (let r = 0; r < shape.length; r++) {
      for (let c = 0; c < shape[0].length; c++) {
        if (!shape[r][c]) continue;

        const baseX = x + c * GRAIN;
        const baseY = y + r * GRAIN;

        for (let gy = 0; gy < GRAIN; gy++) {
          for (let gx = 0; gx < GRAIN; gx++) {
            const X = baseX + gx;
            const Y = baseY + gy;
            if (Y >= 0 && Y < GROWS && X >= 0 && X < GCOLS) next[Y][X] = color;
          }
        }
      }
    }
    return next;
  }

  function sandStep(g) {
    const next = g.map((row) => row.slice());

    for (let y = GROWS - 2; y >= 0; y--) {
      const dir = Math.random() < 0.5 ? -1 : 1;

      if (dir === 1) {
        for (let x = 0; x < GCOLS; x++) {
          const col = next[y][x];
          if (col == null) continue;

          if (next[y + 1][x] == null) {
            next[y + 1][x] = col;
            next[y][x] = null;
          } else {
            const dl = x > 0 && next[y + 1][x - 1] == null;
            const dr = x + 1 < GCOLS && next[y + 1][x + 1] == null;

            if (dl && dr) {
              const pick = Math.random() < 0.5 ? -1 : 1;
              next[y + 1][x + pick] = col;
              next[y][x] = null;
            } else if (dl) {
              next[y + 1][x - 1] = col;
              next[y][x] = null;
            } else if (dr) {
              next[y + 1][x + 1] = col;
              next[y][x] = null;
            }
          }
        }
      } else {
        for (let x = GCOLS - 1; x >= 0; x--) {
          const col = next[y][x];
          if (col == null) continue;

          if (next[y + 1][x] == null) {
            next[y + 1][x] = col;
            next[y][x] = null;
          } else {
            const dl = x > 0 && next[y + 1][x - 1] == null;
            const dr = x + 1 < GCOLS && next[y + 1][x + 1] == null;

            if (dl && dr) {
              const pick = Math.random() < 0.5 ? -1 : 1;
              next[y + 1][x + pick] = col;
              next[y][x] = null;
            } else if (dl) {
              next[y + 1][x - 1] = col;
              next[y][x] = null;
            } else if (dr) {
              next[y + 1][x + 1] = col;
              next[y][x] = null;
            }
          }
        }
      }
    }

    return next;
  }

  function findBridgeClearCells(g) {
    const visited = Array.from({ length: GROWS }, () =>
      Array(GCOLS).fill(false),
    );
    const toClear = [];
    let cleared = 0;

    for (let y = 0; y < GROWS; y++) {
      for (let x = 0; x < GCOLS; x++) {
        const color = g[y][x];
        if (color == null || visited[y][x]) continue;

        const stack = [[x, y]];
        const component = [];
        visited[y][x] = true;

        let touchesLeft = false;
        let touchesRight = false;

        while (stack.length) {
          const [cx, cy] = stack.pop();
          component.push([cx, cy]);
          if (cx === 0) touchesLeft = true;
          if (cx === GCOLS - 1) touchesRight = true;

          const nbrs = [
            [cx + 1, cy],
            [cx - 1, cy],
            [cx, cy + 1],
            [cx, cy - 1],
          ];

          for (const [nx, ny] of nbrs) {
            if (nx < 0 || nx >= GCOLS || ny < 0 || ny >= GROWS) continue;
            if (visited[ny][nx]) continue;
            if (g[ny][nx] !== color) continue;

            visited[ny][nx] = true;
            stack.push([nx, ny]);
          }
        }

        if (touchesLeft && touchesRight) {
          toClear.push(...component);
          cleared += component.length;
        }
      }
    }

    return { toClear, cleared };
  }

  function applyClearCells(g, cells) {
    if (!cells || cells.length === 0) return g;
    const next = g.map((row) => row.slice());
    for (const [x, y] of cells) next[y][x] = null;
    return next;
  }

  function tryStartClear(g) {
    if (clearingRef.current) return false;
    const found = findBridgeClearCells(g);
    if (found.cleared <= 0) return false;

    clearingRef.current = {
      cells: new Set(found.toClear.map(([x, y]) => `${x},${y}`)),
      t: 0,
      dur: CLEAR_DUR_MS,
      points: found.cleared,
    };
    return true;
  }

  function lockPieceAndSpawn() {
    grainsRef.current = stampPieceAsGrains(grainsRef.current, pieceRef.current);
    tryStartClear(grainsRef.current);

    const nextPiece = spawnPiece();
    pieceRef.current = nextPiece;

    if (collidesGrains(grainsRef.current, nextPiece)) {
      overRef.current = true;
      setOver(true);
    }
  }

  function stepPieceDown() {
    if (overRef.current) return;
    if (clearingRef.current) return;

    const g = grainsRef.current;
    const p = pieceRef.current;
    const moved = { ...p, y: p.y + 1 };

    if (!collidesGrains(g, moved)) {
      pieceRef.current = moved;
      return;
    }

    lockPieceAndSpawn();
  }

  function hardDrop() {
    if (overRef.current) return;
    if (clearingRef.current) return;

    const g = grainsRef.current;
    let p = pieceRef.current;

    while (!collidesGrains(g, { ...p, y: p.y + 1 })) {
      p = { ...p, y: p.y + 1 };
    }

    pieceRef.current = p;
    lockPieceAndSpawn();
  }

  function reset() {
    grainsRef.current = emptyGrains();
    pieceRef.current = spawnPiece();
    clearingRef.current = null;

    scoreRef.current = 0;
    setScore(0);

    overRef.current = false;
    setOver(false);

    lastTimeRef.current = 0;
    fallAccumRef.current = 0;
    frameRef.current = 0;
  }

  useEffect(() => {
    if (!pieceRef.current) pieceRef.current = spawnPiece();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "r" || e.key === "R") return reset();
      if (e.key === " " && !e.repeat) {
        e.preventDefault();
        return hardDrop();
      }

      if (overRef.current) return;
      if (clearingRef.current) return;

      const g = grainsRef.current;
      const p = pieceRef.current;

      if (e.key === "ArrowLeft") {
        const np = { ...p, x: p.x - GRAIN };
        if (!collidesGrains(g, np)) pieceRef.current = np;
      } else if (e.key === "ArrowRight") {
        const np = { ...p, x: p.x + GRAIN };
        if (!collidesGrains(g, np)) pieceRef.current = np;
      } else if (e.key === "ArrowDown") {
        const np = { ...p, y: p.y + 2 };
        if (!collidesGrains(g, np)) pieceRef.current = np;
      } else if (e.key === "ArrowUp") {
        const rs = rotate(p.shape);
        const np = { ...p, shape: rs };

        if (!collidesGrains(g, np)) pieceRef.current = np;
        else if (!collidesGrains(g, { ...np, x: np.x - GRAIN }))
          pieceRef.current = { ...np, x: np.x - GRAIN };
        else if (!collidesGrains(g, { ...np, x: np.x + GRAIN }))
          pieceRef.current = { ...np, x: np.x + GRAIN };
        else if (!collidesGrains(g, { ...np, x: np.x - 2 * GRAIN }))
          pieceRef.current = { ...np, x: np.x - 2 * GRAIN };
        else if (!collidesGrains(g, { ...np, x: np.x + 2 * GRAIN }))
          pieceRef.current = { ...np, x: np.x + 2 * GRAIN };
      }
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const loop = (t) => {
      if (!lastTimeRef.current) lastTimeRef.current = t;
      const dt = t - lastTimeRef.current;
      lastTimeRef.current = t;

      frameRef.current++;

      const clearing = clearingRef.current;
      if (clearing) {
        clearing.t += dt;

        if (clearing.t >= clearing.dur) {
          // remove cleared cells
          const cells = [];
          for (const key of clearing.cells) {
            const [x, y] = key.split(",").map(Number);
            cells.push([x, y]);
          }
          grainsRef.current = applyClearCells(grainsRef.current, cells);

          // score: +1 per grain removed (sync ref + state)
          scoreRef.current += clearing.points;
          setScore(scoreRef.current);

          clearingRef.current = null;
        }
      } else {
        if (!overRef.current) {
          fallAccumRef.current += dt;
          if (fallAccumRef.current >= TICK_MS) {
            fallAccumRef.current %= TICK_MS;
            stepPieceDown();
          }
        }

        for (let i = 0; i < SAND_ITERS_PER_FRAME; i++) {
          grainsRef.current = sandStep(grainsRef.current);
        }

        if (frameRef.current % CLEAR_SCAN_EVERY_N_FRAMES === 0) {
          tryStartClear(grainsRef.current);
        }
      }

      draw();
      rafRef.current = requestAnimationFrame(loop);
    };

    rafRef.current = requestAnimationFrame(loop);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      lastTimeRef.current = 0;
      fallAccumRef.current = 0;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function draw() {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    const grains = grainsRef.current;
    const piece = pieceRef.current;
    const clearing = clearingRef.current;

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = "rgba(0,0,0,0.35)";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    ctx.globalAlpha = 0.18;
    ctx.strokeStyle = "rgba(255,255,255,0.15)";
    for (let x = 0; x <= COLS; x++) {
      ctx.beginPath();
      ctx.moveTo(x * GRAIN * SCALE, 0);
      ctx.lineTo(x * GRAIN * SCALE, GROWS * SCALE);
      ctx.stroke();
    }
    for (let y = 0; y <= ROWS; y++) {
      ctx.beginPath();
      ctx.moveTo(0, y * GRAIN * SCALE);
      ctx.lineTo(GCOLS * SCALE, y * GRAIN * SCALE);
      ctx.stroke();
    }
    ctx.globalAlpha = 1;

    for (let y = 0; y < GROWS; y++) {
      for (let x = 0; x < GCOLS; x++) {
        const col = grains[y][x];
        if (!col) continue;

        let a = 1;
        if (clearing?.cells?.has(`${x},${y}`)) {
          const p = Math.min(1, clearing.t / clearing.dur);
          const blink = Math.floor(p * 8) % 2 ? 0.35 : 1;
          a = (1 - p) * blink;
        }

        drawGrain(ctx, x, y, SCALE, col, a);
      }
    }

    if (piece && !clearing) {
      const { shape, x: px, y: py, color } = piece;
      for (let r = 0; r < shape.length; r++) {
        for (let c = 0; c < shape[0].length; c++) {
          if (!shape[r][c]) continue;
          const baseX = px + c * GRAIN;
          const baseY = py + r * GRAIN;
          for (let gy = 0; gy < GRAIN; gy++) {
            for (let gx = 0; gx < GRAIN; gx++) {
              const X = baseX + gx;
              const Y = baseY + gy;
              if (X < 0 || X >= GCOLS || Y < 0 || Y >= GROWS) continue;
              drawGrain(ctx, X, Y, SCALE, color, 1);
            }
          }
        }
      }
    }

    ctx.fillStyle = "#FFFF03";
    ctx.font = "12px ui-monospace, Menlo, Consolas, monospace";
    ctx.fillText(`SCORE: ${scoreRef.current}`, 8, 16);
    if (overRef.current) ctx.fillText("GAME OVER — press R", 8, 34);
  }

  function drawGrain(ctx, gx, gy, s, base, alpha = 1) {
    const px = gx * s;
    const py = gy * s;

    ctx.save();
    ctx.globalAlpha = alpha;

    ctx.fillStyle = base;
    ctx.fillRect(px, py, s, s);

    ctx.fillStyle = "rgba(255,255,255,0.10)";
    ctx.fillRect(px, py, s, 1);
    ctx.fillRect(px, py, 1, s);

    ctx.fillStyle = "rgba(0,0,0,0.12)";
    ctx.fillRect(px + s - 1, py, 1, s);
    ctx.fillRect(px, py + s - 1, s, 1);

    ctx.restore();
  }

  return (
    <div className="sandtris">
      <canvas
        ref={canvasRef}
        width={GCOLS * SCALE}
        height={GROWS * SCALE}
        className="sandtris-canvas"
      />
    </div>
  );
}
